import keys from '@debbie/common/dist/cache/keys.js'
import { CaseVoucher } from '@debbie/common/dist/economy/case-vouchers/index.js'
import { suggestedPlans as mapVoucherToPlans } from '@debbie/common/dist/economy/helpers/payment-plans/index.js'
import * as paymentPlanHelpers from '@debbie/common/dist/economy/payment-plans/index.js'
import { Currency } from '@debbie/common/dist/index.js'
import { paymentPlans as paymentPlansApi } from '@debbie/cortex/dist/api/index.js'
import { useCaseConditions } from '@debbie/cortex/dist/case-conditions/index.js'
import {
  casesEntity,
  ensureCaseLoaded,
  useCase,
} from '@debbie/cortex/dist/cases/index.js'
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js'
import { useLimitationDocuments } from '@debbie/cortex/dist/documents/index.js'
import { useFixedDistributionsByCaseId } from '@debbie/cortex/dist/fixed-distributions/index.js'
import { c, h } from '@debbie/cortex/dist/index.js'
import { paymentPlansEntity } from '@debbie/cortex/dist/paymentPlans/index.js'
import q from '@debbie/cortex/dist/query.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import { useTenant } from '@debbie/cortex/dist/tenant/index.js'
import { Panel, PlaceholderLoader } from '@debbie/iris/dist/components/index.js'
import PaymentPlanSuggestions from '@debbie/iris/dist/components/PaymentPlan/components/PaymentPlanSuggestions.js'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

interface Props {
  caseId: string
  caseVouchers: CaseVoucher[]
  currency: Currency
}

const SelectPlanStep = (props: Props) => {
  const { caseId, caseVouchers } = props

  const lang = useSelector((s: RootState) => s.lang)
  const isAuthTokenLoaded = useSelector((s: RootState) =>
    h.isLoaded(s.authToken),
  )

  const singleCaseLoader = useCase(caseId)
  const singleCase = h.hasData(singleCaseLoader) ? singleCaseLoader.data : null

  const tenant = useTenant()
  const creditorLoadable = useCreditor(singleCase?.creditorId)
  const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null
  const documentsLoader = useLimitationDocuments(singleCase?.caseId)
  const documents = h.hasData(documentsLoader) ? documentsLoader.data : null
  const fixedDistributionsLoader = useFixedDistributionsByCaseId(
    singleCase?.caseId,
  )
  const fixedDistributions = h.hasData(fixedDistributionsLoader)
    ? fixedDistributionsLoader.data
    : null
  const conditionsLoader = useCaseConditions(caseId)
  const conditions = h.hasData(conditionsLoader) ? conditionsLoader.data : null

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [loadedSuggestedPlans, setLoadedSuggestedPlans] =
    useState<boolean>(false)
  const [suggestedPlans, setSuggestedPlans] = useState<any[]>([])
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)

  const selectPlan = async (planId: string) => {
    setIsLoading(true)

    if (isAuthTokenLoaded) {
      await paymentPlansApi
        .create(caseId, {
          selectedPaymentPlanId: planId,
          activationTrigger: true,
          config: null,
        })
        .catch((err) => {
          console.error('Error creating payment plan:', err)
          setIsLoading(false)
          alert('Error. Please try again later.')
        })

      q.invalidate(keys.write.create(paymentPlansEntity))
      q.invalidate(keys.write.modify(casesEntity, caseId))
      await ensureCaseLoaded(caseId)
    }

    setIsLoading(false)
  }

  useEffect(() => {
    if (
      caseVouchers &&
      caseVouchers.length > 0 &&
      singleCase &&
      documents &&
      tenant &&
      creditor &&
      fixedDistributions &&
      conditions
    ) {
      const loadSuggestions = async () => {
        const sugg = await mapVoucherToPlans(
          caseVouchers,
          documents,
          tenant,
          creditor,
          singleCase.economyEngine,
          fixedDistributions,
          conditions,
        )
        setLoadedSuggestedPlans(true)
        setSuggestedPlans(sugg)
      }

      loadSuggestions()
    }
  }, [
    caseVouchers,
    singleCase,
    tenant,
    creditor,
    documents,
    fixedDistributions,
    conditions,
  ])

  const paymentPlanConfig = paymentPlanHelpers.resolvePaymentPlansConfig(
    creditor?.paymentPlansConfig,
    tenant?.paymentPlansConfig,
  )

  return (
    <Panel header={lang.debtorWebPaymentPlans.selectPlan}>
      {h.isError(conditionsLoader) ? (
        <h2>{lang.costCeiling.failedToLoad}</h2>
      ) : isLoading ? (
        <PlaceholderLoader />
      ) : suggestedPlans ? (
        (suggestedPlans.length === 0 && loadedSuggestedPlans) ||
        paymentPlanConfig.disablePaymentPlans ||
        singleCase?.settings?.disableDebtorPaymentPlan ? (
          <h2>{lang.paymentPlans.cannotOfferPaymentPlan}</h2>
        ) : (
          singleCase && (
            <>
              <PaymentPlanSuggestions
                caseVouchers={caseVouchers}
                singleCase={singleCase}
                standardPlans={paymentPlanConfig.plans.filter((sp) =>
                  suggestedPlans.some((s) => s.id === sp.id),
                )}
                selectedPlan={selectedPlanId}
                onSelectPlan={(id) => {
                  if (id) {
                    selectPlan(id)
                  }
                }}
              />
            </>
          )
        )
      ) : (
        <div>{lang.debtorWebPaymentPlans.noOptions}</div>
      )}
    </Panel>
  )
}

export default SelectPlanStep
